<template>
  <MainWrapperMobile>
    <template #header>
      <RepositoriesSheet>
        <template #default="{ repositoryName, repositoryOwner, openRepositoriesSheet }">
          <DSHeaderRepositorySwitcher
            v-if="repositoryName"
            :name="repositoryName"
            :owner="repositoryOwner"
            @click="openRepositoriesSheet"
          />
        </template>
      </RepositoriesSheet>
    </template>
    <template #default="{ drawerOpen, updateDrawerOpen, isEmptyApp }">
      <MainMobileMenu
        :open="drawerOpen"
        @update:open="updateDrawerOpen"
      >
        <template
          v-if="!isReadonly && !isEmptyApp"
          #top
        >
          <DSSidebarItem
          
            label="Add asset"
            :icon="PlusIcon"
            class="h-12 text-[15px] w-full justify-center bg-white/10 hover:bg-white/15 border border-white/15"
            @click="updateDrawerOpen(false); handleAddAsset()"
          />
        </template>
        <DSSidebarItem
          v-if="isAdvisorMode"
          label="Advisor"
          :icon="LandmarkIcon"
          :to="{ name: ROUTE_NAME.ADVISOR }"
          :active="route.name === ROUTE_NAME.ADVISOR"
          is-mobile
          class="w-full justify-start shrink-0"
        />
        <NavbarGroup
          v-for="group in navbarItems"
          :key="group.label"
          :group
          is-mobile
          :is-readonly
          @click="updateDrawerOpen(false)"
        />
        <template #footer>
          <NavbarRepositories
            is-mobile
            @click="updateDrawerOpen(false)"
          />
        </template>
      </MainMobileMenu>
    </template>
    <template #content>
      <div class="flex items-center bg-background py-3 -mx-4 px-4">
        <DSButton
          variant="secondary"
          size="sm"
          class="rounded-full"
          @click="assetTreeOpen = true"
        >
          <ListTreeIcon />
          Asset tree
          <ChevronDownIcon />
        </DSButton>
      </div>
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>
      <MobileSheet
        v-model:open="assetTreeOpen"
        title="Asset tree"
      >
        <AssetsTree class="min-h-96" />
      </MobileSheet>
    </template>
  </MainWrapperMobile>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, markRaw, ref } from 'vue'
import { useRoute } from 'vue-router'

import { NavigationItem, ReadonlyMode } from '@types'

import { ADVISOR_MODE, READONLY_MODE, ROUTE_NAME } from '@/const'

import { useModalsStore } from '@/store/modals'
import { useNavigationStore } from '@/store/navigation'

import AddAssetDialog from '@/modules/AddAsset/AddAssetDialog.vue'
import MainWrapperMobile from './components/MainWrapperMobile.vue'
import MainMobileMenu from './components/MainMobileMenu.vue'
import NavbarGroup from './components/NavbarGroup.vue'
import RepositoriesSheet from './components/RepositoriesSheet.vue'
import { DSButton, DSHeaderRepositorySwitcher, DSSidebarItem } from '@ds'
import { ChevronDownIcon, LandmarkIcon, ListTreeIcon, PlusIcon } from '@lucide/vue'
import NavbarRepositories from './components/NavbarRepositories.vue'
import MobileSheet from './components/MobileSheet.vue'
import AssetsTree from './components/AssetsTree.vue'

const route = useRoute()

const modalsStore = useModalsStore()
const navigationStore = useNavigationStore()

const isAdvisorMode = inject<ComputedRef<boolean>>(ADVISOR_MODE)
const isReadonly = inject<ReadonlyMode>(READONLY_MODE)

const assetTreeOpen = ref(false)

const navbarItems = computed<NavigationItem[]>(() => navigationStore.getParentNavigation)

const handleAddAsset = () => {
  if (isReadonly?.value) return
  const modalInstance = modalsStore.init('main-add-asset', markRaw(AddAssetDialog))
  modalInstance?.open(modalsStore.getZIndex())
}
</script>
